import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { ErroApi, api, type PerfilNome } from '../dados/api.js'
import { buscarNoDiretorio, conectarDiretorio, type PessoaDiretorio } from '../dados/diretorio.js'
import { PrecisaConectar, traduzirErroDeLogin } from '../dados/login.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { COMANDO, COMUNS, EXPLICACAO } from './PerfisUsuario.js'
import { EscolhaObras } from './EscolhaObras.js'
import { rotuloPerfil } from './admin-comum.js'

/// Cadastro de uma pessoa pela tela.
///
/// Para quem não veio na migração do Power Apps, e para deixar perfil e obras
/// prontos antes do primeiro login. A pessoa é escolhida no diretório da Ápia
/// (Entra ID): nome e e-mail vêm de lá, sem digitação, e o cadastro já nasce
/// ligado à conta Microsoft dela. Digitar à mão continua possível, para quando o
/// diretório não responder. Quando o e-mail já existe, o servidor não
/// mexe no cadastro — a tela leva até ele, que é onde a decisão certa está.

/// Perfis que já enxergam todas as obras; para eles, escolher obra não muda nada.
const VEEM_TUDO: PerfilNome[] = ['SUPERADMIN', 'ADMIN', 'ENGENHEIRO_SEGURANCA', 'GERENTE_CONTRATO']

export function NovoUsuario({
  grupoAcesso,
  aoMostrar,
}: {
  grupoAcesso: string | null
  /// Leva a lista até o cadastro, recém-criado ou já existente.
  aoMostrar: (email: string, id?: string) => void
}) {
  const cliente = useQueryClient()
  const { usuario: eu } = useUsuarioAtual()
  const souSupremo = eu?.superAdmin ?? false

  const [aberto, setAberto] = useState(false)
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [perfis, setPerfis] = useState<PerfilNome[]>([])
  const [obraIds, setObraIds] = useState<string[]>([])
  const [motivo, setMotivo] = useState('')
  const [vinculado, setVinculado] = useState<PessoaDiretorio | null>(null)
  const [criado, setCriado] = useState<{ id: string; nome: string; email: string } | null>(null)

  const criar = useMutation({
    mutationFn: () =>
      api.adminCriarUsuario({
        nome: nome.trim(),
        email: email.trim(),
        entraOid: vinculado?.oid,
        perfis,
        obraIds,
        motivo: motivo.trim() || undefined,
      }),
    onSuccess: (r) => {
      setCriado(r)
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  function limpar() {
    setNome('')
    setEmail('')
    setPerfis([])
    setObraIds([])
    setMotivo('')
    setVinculado(null)
    setCriado(null)
    criar.reset()
  }

  if (!aberto) {
    return (
      <button onClick={() => setAberto(true)} className="botao-primario mb-3 w-full !min-h-0 !py-2 text-sm">
        + Novo usuário
      </button>
    )
  }

  if (criado) {
    return (
      <div className="cartao mb-4 border-leve/30 bg-leve/5 p-3">
        <p className="text-sm font-semibold text-leve">{criado.nome} foi cadastrado com acesso.</p>
        <p className="mt-1 text-xs text-slate-600">
          {grupoAcesso
            ? `Para entrar, ${criado.email} também precisa estar no grupo "${grupoAcesso}" do Entra ID.`
            : `Já pode entrar com a conta Microsoft ${criado.email}.`}{' '}
          O registro ficou na aba Auditoria.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => {
              aoMostrar(criado.email, criado.id)
              limpar()
              setAberto(false)
            }}
            className="botao-neutro flex-1 !min-h-0 !py-1.5 text-sm"
          >
            Ver cadastro
          </button>
          <button onClick={limpar} className="botao-neutro flex-1 !min-h-0 !py-1.5 text-sm">
            Cadastrar outro
          </button>
        </div>
      </div>
    )
  }

  const erro = criar.error as Error | null
  const existente =
    erro instanceof ErroApi && erro.status === 409
      ? (erro.corpo as { id?: string; email?: string } | undefined)
      : undefined
  const veTudo = perfis.some((p) => VEEM_TUDO.includes(p))
  const podeEnviar = nome.trim().length >= 3 && /\S+@\S+\.\S+/.test(email.trim()) && !criar.isPending

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (podeEnviar) criar.mutate()
      }}
      className="cartao mb-4 space-y-3 p-3"
    >
      <p className="font-semibold text-slate-800">Novo usuário</p>

      {vinculado ? (
        <div className="flex items-start gap-2 rounded-lg bg-leve/10 px-3 py-2 text-sm">
          <span className="text-leve">✓</span>
          <span className="min-w-0 flex-1 text-slate-700">
            Conta Microsoft de <strong>{vinculado.nome}</strong>
            {vinculado.convidado && ' (convidado de outra empresa)'}. O cadastro fica ligado a ela.
          </span>
          <button
            type="button"
            onClick={() => setVinculado(null)}
            className="shrink-0 text-xs font-semibold text-marinho hover:underline"
          >
            Trocar
          </button>
        </div>
      ) : (
        <BuscaDiretorio
          aoEscolher={(p) => {
            setVinculado(p)
            setNome(p.nome)
            setEmail(p.email)
          }}
        />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Nome completo</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} className="campo mt-1" />
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-slate-600">E-mail da conta Microsoft</span>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              // E-mail mexido à mão não é mais o da conta escolhida.
              if (vinculado) setVinculado(null)
            }}
            placeholder="nome.sobrenome@grupoapia.com.br"
            className="campo mt-1"
            autoComplete="off"
          />
          <span className="mt-1 block text-xs text-slate-500">
            O mesmo com que a pessoa entra no Outlook e no Teams. Com outro, o login não a reconhece.
          </span>
        </label>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold text-slate-600">Perfis</p>
        <div className="grid gap-1 lg:grid-cols-2">

          {[...COMANDO, ...COMUNS].map((p) => {
            const travado = COMANDO.includes(p) && !souSupremo
            return (
              <label
                key={p}
                className={`flex items-start gap-2 rounded p-1.5 ${travado ? 'opacity-60' : 'hover:bg-slate-50'}`}
              >
                <input
                  type="checkbox"
                  checked={perfis.includes(p)}
                  disabled={travado}
                  onChange={() =>
                    setPerfis((atual) => (atual.includes(p) ? atual.filter((x) => x !== p) : [...atual, p]))
                  }
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800">{rotuloPerfil[p]}</span>
                  {EXPLICACAO[p] && <span className="block text-xs text-slate-500">{EXPLICACAO[p]}</span>}
                </span>
              </label>
            )
          })}
        </div>
        {perfis.length === 0 && (
          <p className="mt-1 text-xs text-slate-500">Sem nenhum marcado, a pessoa entra como colaborador.</p>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold text-slate-600">Obras</p>
        {veTudo ? (
          <p className="text-xs text-slate-500">Pelo perfil escolhido, a pessoa já enxerga todas as obras.</p>
        ) : (
          <>
            <EscolhaObras selecionadas={obraIds} aoMudar={setObraIds} />
            {obraIds.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Sem obra, a pessoa entra mas não vê nenhuma inspeção. Dá para escolher depois.
              </p>
            )}
          </>
        )}
      </div>

      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Motivo (fica no registro)</span>
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: técnico de segurança contratado para a obra 0101"
          className="campo mt-1"
        />
      </label>

      {grupoAcesso && (
        <p className="rounded bg-marinho-50 px-3 py-2 text-xs text-marinho">
          Quem decide a entrada é o grupo <strong>{grupoAcesso}</strong> do Entra ID. Este cadastro
          deixa perfil e obras prontos, mas a pessoa só entra depois de estar no grupo.
        </p>
      )}

      {erro && (
        <div className="rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
          <p>{erro.message}</p>
          {existente?.email && (
            <button
              type="button"
              onClick={() => {
                aoMostrar(existente.email!, existente.id)
                limpar()
                setAberto(false)
              }}
              className="mt-1 font-semibold underline"
            >
              Mostrar o cadastro
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={!podeEnviar} className="botao-primario flex-1">
          {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
        </button>
        <button
          type="button"
          onClick={() => {
            limpar()
            setAberto(false)
          }}
          className="botao-neutro flex-1"
        >
          Cancelar
        </button>
      </div>
    </form>
  )
}

/// Busca de pessoas no diretório da Ápia, pela conta de quem está cadastrando.
function BuscaDiretorio({ aoEscolher }: { aoEscolher: (p: PessoaDiretorio) => void }) {
  const [termo, setTermo] = useState('')
  const [adiado, setAdiado] = useState('')
  const [conectando, setConectando] = useState(false)
  const [erroConexao, setErroConexao] = useState<string | null>(null)

  // Uma consulta por pausa na digitação, e não uma por tecla.
  useEffect(() => {
    const t = setTimeout(() => setAdiado(termo.trim()), 350)
    return () => clearTimeout(t)
  }, [termo])

  const consulta = useQuery({
    queryKey: ['diretorio', adiado],
    queryFn: () => buscarNoDiretorio(adiado),
    enabled: adiado.length >= 3,
    retry: false,
    staleTime: 5 * 60_000,
  })

  async function conectar() {
    setConectando(true)
    setErroConexao(null)
    try {
      await conectarDiretorio()
      await consulta.refetch()
    } catch (e) {
      setErroConexao(traduzirErroDeLogin(e))
    } finally {
      setConectando(false)
    }
  }

  const precisaConectar = consulta.error instanceof PrecisaConectar
  const outroErro = erroConexao ?? (consulta.error && !precisaConectar ? (consulta.error as Error).message : null)

  return (
    <div className="rounded-lg border border-marinho/15 bg-marinho-50/60 p-2">
      <p className="mb-1 text-xs font-semibold text-slate-600">Buscar no diretório da Ápia (Entra ID)</p>
      <CampoBusca valor={termo} aoMudar={setTermo} placeholder="Nome ou e-mail da pessoa" autoFocus />

      {termo.trim().length > 0 && termo.trim().length < 3 && (
        <p className="mt-1 text-xs text-slate-500">Digite pelo menos 3 letras.</p>
      )}
      {consulta.isFetching && <p className="mt-1 text-xs text-slate-500">Buscando…</p>}

      {precisaConectar && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span>Para buscar no diretório, confirme sua conta Microsoft.</span>
          <button
            type="button"
            onClick={() => void conectar()}
            disabled={conectando}
            className="botao-neutro !min-h-0 !py-1 text-xs"
          >
            {conectando ? 'Abrindo…' : 'Conectar'}
          </button>
        </div>
      )}
      {outroErro && (
        <p className="mt-1 text-xs text-alta" role="alert">
          {outroErro} Dá para preencher nome e e-mail à mão abaixo.
        </p>
      )}

      {consulta.data && adiado.length >= 3 && !consulta.isFetching && (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          {consulta.data.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-500">Ninguém encontrado com "{adiado}".</li>
          ) : (
            consulta.data.map((p) => (
              <li key={p.oid}>
                <button
                  type="button"
                  onClick={() => {
                    aoEscolher(p)
                    setTermo('')
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-marinho-50"
                >
                  <span className="block text-sm font-medium text-slate-800">
                    {p.nome}
                    {p.convidado && <span className="etiqueta ml-2 bg-slate-100 text-slate-500">convidado</span>}
                  </span>
                  <span className="block text-xs text-slate-500">{p.email}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
